import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';
import { ArrowRight, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { getDeviceFingerprint } from '../utils/deviceFingerprint';
import { roleHome } from '../utils/roleHome';
// Phase W0 — shared host→slug resolution (handles white-label apexes too).
import { getTenantSlug, DEMO_SLUG_KEY } from '../utils/tenantHost';
import { apiError } from '../utils/apiError';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Input with brand-colored focus ring — no layout change, just better UX
function BrandedInput({ primaryColor, className = '', ...props }) {
  const [focused, setFocused] = useState(false);
  return (
    <input
      {...props}
      onFocus={e => { setFocused(true); props.onFocus?.(e); }}
      onBlur={e => { setFocused(false); props.onBlur?.(e); }}
      className={`w-full px-4 py-3 bg-slate-50 border rounded-lg outline-none transition-all ${className}`}
      style={{
        borderColor: focused ? primaryColor : '#e2e8f0',
        boxShadow:   focused ? `0 0 0 3px ${primaryColor}20` : 'none',
      }}
    />
  );
}

export default function Login() {
  // Belt and braces for the session-expiry paths above: whoever lands on the
  // login form must sign in to the tenant this HOST belongs to. A leftover
  // demo_tenant_slug (profile menu → Switch Domain) without a token is stale
  // by definition (see utils/tenantHost.js) and would route the credentials to
  // the wrong tenant's user table.
  useEffect(() => {
    if (!localStorage.getItem('token')) {
      try { localStorage.removeItem(DEMO_SLUG_KEY); } catch { /* private mode */ }
    }
  }, []);
  const [email,       setEmail]       = useState('');
  const [password,    setPassword]    = useState('');
  const [showPass,    setShowPass]    = useState(false);
  const [loading,     setLoading]     = useState(false);
  const [twoFAStep,   setTwoFAStep]   = useState(false);
  const [ipStep,      setIpStep]      = useState(false);
  const [tempToken,   setTempToken]   = useState('');
  const [totpCode,    setTotpCode]    = useState('');
  const [ipOtp,       setIpOtp]       = useState('');
  const [pendingIp,   setPendingIp]   = useState('');
  const [fallbackOtp, setFallbackOtp] = useState('');
  const [otpChannel,  setOtpChannel]  = useState('email');
  const [phoneHint,   setPhoneHint]   = useState('');
  // "Trust this browser for 30 days" — checked by default, Amazon-style.
  // Unchecking forces re-verification on every login (high-security mode).
  const [trustDevice, setTrustDevice] = useState(true);

  // `setUser` is needed so the 2FA / IP-verification handlers can update
  // AuthContext's React state — not just localStorage. Without it the post-
  // verification navigate() lands on a ProtectedRoute that still sees
  // user=null in React state and bounces back to /login.
  const { login, setUser } = useAuth();
  const navigate  = useNavigate();
  const branding  = useBranding();

  const primaryColor = branding?.primary_color || '#42a4bf';
  const companyName  = branding?.name || '';
  const isClient     = window.location.pathname.includes('/client');

  const goHome = (role) => {
    // Canonical role→home map lives in utils/roleHome (shared with ProtectedRoute).
    navigate(roleHome(role));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Send the device fingerprint so the backend can short-circuit
      // the new-location OTP if this browser was previously trusted on
      // the current /24 network. Falls back to '' on old browsers, in
      // which case the backend treats this as an untrusted device and
      // proceeds with the legacy IP-only check.
      const device_fingerprint = await getDeviceFingerprint();
      // Trim + lowercase — mobile keyboards autocapitalize and autocomplete
      // appends a trailing space; the backend normalizes too, but sending it
      // clean keeps every downstream consumer (2FA, IP-OTP flows) consistent.
      const user = await login({ email: email.trim().toLowerCase(), password, device_fingerprint });
      if (user?.requires_2fa) {
        setTempToken(user.temp_token); setTwoFAStep(true); setLoading(false); return;
      }
      if (user?.requires_ip_verification) {
        setTempToken(user.temp_token); setPendingIp(user.ip || '');
        setOtpChannel(user.channel || 'email'); setPhoneHint(user.phone_hint || '');
        setFallbackOtp(user.fallback_otp || '');
        if (user.fallback_otp) setIpOtp(user.fallback_otp);
        setIpStep(true); setLoading(false); return;
      }
      if (user?.password_expired) {
        toast.error('Your password has expired. Please reset it.');
        navigate('/forgot-password'); return;
      }
      toast.success('Login successful!');
      goHome(user.role);
    } catch (err) {
      toast.error(apiError(err, 'Login failed'));
    } finally { setLoading(false); }
  };

  const handleIpVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const slug = getTenantSlug();
      const headers = { 'Content-Type': 'application/json' };
      if (slug) headers['X-Tenant-Slug'] = slug;
      // Capture browser fingerprint + UA so the backend can store a
      // trust record on success (only when "Trust this browser" stayed
      // checked — passed via trust_device).
      const device_fingerprint = await getDeviceFingerprint();
      const { data } = await axios.post(`${BACKEND_URL}/api/auth/verify-ip`,
        {
          temp_token:         tempToken,
          otp:                ipOtp,
          ip_label:           'My device',
          trust_device:       trustDevice,
          device_fingerprint,
          user_agent:         navigator.userAgent || '',
        },
        { headers });
      localStorage.setItem('token', data.token);
      localStorage.setItem('last_active_at', Date.now().toString());
      // setUser also writes localStorage 'user' AND updates AuthContext's
      // React state. Doing setItem('user', ...) directly would skip the
      // React update, leaving ProtectedRoute seeing user=null and
      // bouncing the post-verify navigate() back to /login.
      setUser(data.user);
      toast.success('Login successful!');
      goHome(data.user.role);
    } catch (err) {
      toast.error(apiError(err, 'Invalid code. Try again.'));
      setIpOtp('');
    } finally { setLoading(false); }
  };

  const handle2FA = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const slug = getTenantSlug();
      const headers = { 'Content-Type': 'application/json' };
      if (slug) headers['X-Tenant-Slug'] = slug;
      const { data } = await axios.post(`${BACKEND_URL}/api/auth/2fa/verify`,
        { temp_token: tempToken, code: totpCode }, { headers });
      localStorage.setItem('token', data.token);
      localStorage.setItem('last_active_at', Date.now().toString());
      // Same React-state-must-update reason as in handleIpVerify above.
      setUser(data.user);
      toast.success('Login successful!');
      goHome(data.user.role);
    } catch (err) {
      toast.error(apiError(err, 'Invalid code. Try again.'));
      setTotpCode('');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">

        {/* Logo / Company name — fixed 240×80 container matches the
            "Recommended: 240×80 px" guidance shown on Settings → Branding.
            Every tenant's uploaded image scales to fit this box via
            max-w-full + max-h-full + object-contain, so a tight-cropped
            wordmark (Empenhanz, Euphoric AI) and a padded icon+text logo
            (Immiways) both occupy the same visual footprint. Single
            standard across the entire platform. */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-60 h-20 mb-4">
            {branding?.logo_url ? (
              <img
                src={branding.logo_url}
                alt={companyName || 'Logo'}
                className="max-w-full max-h-full object-contain"
                data-testid="app-logo"
              />
            ) : companyName ? (
              <span
                style={{ color: primaryColor, fontWeight: 700, fontSize: 22, letterSpacing: 1 }}
                data-testid="app-logo"
              >
                {companyName.toUpperCase()}
              </span>
            ) : (
              /* Default: Euphoric AI OS branding when no tenant is configured */
              <div className="flex flex-col items-center gap-1" data-testid="app-logo">
                <img
                  src="/euphoric-logo.png"
                  alt="Euphoric AI OS"
                  className="max-w-full max-h-12 object-contain"
                  onError={e => { e.target.style.display = 'none'; }}
                />
                <span style={{ color: '#42a4bf', fontWeight: 700, fontSize: 14, letterSpacing: 1 }}>
                  EUPHORIC AI OS
                </span>
              </div>
            )}
          </div>
          {branding?.tagline && (
            <p className="text-slate-500 text-sm">{branding.tagline}</p>
          )}
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8" data-testid="login-form-container">

          {/* ── IP Verification ── */}
          {ipStep ? (
            <>
              <div className="text-center mb-6">
                <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3"
                  style={{ backgroundColor: primaryColor + '20' }}>
                  <span className="text-2xl">🛡️</span>
                </div>
                <h2 className="text-xl font-bold text-slate-900">New Location Detected</h2>
                <p className="text-slate-500 text-sm mt-1">
                  Login from <span className="font-mono text-slate-700">{pendingIp}</span>
                </p>
              </div>

              {otpChannel === 'on_screen' ? (
                <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-center">
                  <p className="text-xs font-medium text-amber-700 uppercase tracking-wide mb-2">
                    ⚠️ Email & SMS unavailable
                  </p>
                  <p className="text-xs text-amber-600 mb-3">
                    We couldn't reach you. Your one-time code is below — note it down and update your contact details after logging in.
                  </p>
                  <p className="text-3xl font-mono font-bold tracking-[0.25em] text-amber-900">{fallbackOtp}</p>
                  <p className="text-xs text-amber-500 mt-2">Expires in 15 minutes</p>
                </div>
              ) : otpChannel === 'sms' ? (
                <p className="text-slate-500 text-sm text-center mb-4">
                  A verification code was sent via <strong>SMS</strong> to {phoneHint} — check your messages.
                </p>
              ) : (
                <p className="text-slate-500 text-sm text-center mb-4">
                  A verification code was sent to your <strong>email</strong> — check your inbox.
                </p>
              )}

              <form onSubmit={handleIpVerify} className="space-y-4">
                <BrandedInput
                  primaryColor={primaryColor}
                  type="text" inputMode="numeric" maxLength={6}
                  placeholder="000000"
                  value={ipOtp}
                  onChange={e => setIpOtp(e.target.value.replace(/\D/g, ''))}
                  className="text-center text-2xl font-mono tracking-widest"
                  autoFocus required
                />
                {!fallbackOtp && (
                  <p className="text-xs text-slate-400 text-center">Code expires in 15 minutes</p>
                )}

                {/* Trust-this-browser toggle — Amazon-style. Default ON so
                    the typical user doesn't have to re-verify every login.
                    Unchecking gives them the old high-security behavior. */}
                <label className="flex items-start gap-2.5 px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl cursor-pointer hover:bg-slate-100 transition-colors">
                  <input
                    type="checkbox"
                    checked={trustDevice}
                    onChange={e => setTrustDevice(e.target.checked)}
                    className="mt-0.5 w-4 h-4 rounded shrink-0"
                    style={{ accentColor: primaryColor }}
                  />
                  <div className="text-left">
                    <p className="text-sm font-medium text-slate-800">Trust this browser for 30 days</p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Skip this verification next time you log in from this browser on the same network.
                    </p>
                  </div>
                </label>

                <button type="submit" disabled={loading || ipOtp.length !== 6}
                  style={{ backgroundColor: primaryColor }}
                  className="w-full py-3 text-white font-semibold rounded-xl disabled:opacity-60 transition-all hover:opacity-90">
                  {loading ? 'Verifying...' : (trustDevice ? 'Verify & Trust This Browser' : 'Verify Code')}
                </button>
                <button type="button"
                  onClick={() => { setIpStep(false); setIpOtp(''); setFallbackOtp(''); setOtpChannel('email'); setPhoneHint(''); }}
                  className="w-full py-2 text-slate-500 text-sm hover:text-slate-700">
                  ← Back to login
                </button>
              </form>
            </>

          /* ── 2FA ── */
          ) : twoFAStep ? (
            <>
              <div className="text-center mb-6">
                <div className="w-14 h-14 rounded-full bg-teal-50 flex items-center justify-center mx-auto mb-3">
                  <span className="text-2xl">🔐</span>
                </div>
                <h2 className="text-xl font-bold text-slate-900">Two-Factor Authentication</h2>
                <p className="text-slate-500 text-sm mt-1">Enter the 6-digit code from your authenticator app</p>
              </div>
              <form onSubmit={handle2FA} className="space-y-4">
                <BrandedInput
                  primaryColor={primaryColor}
                  type="text" inputMode="numeric" maxLength={6}
                  placeholder="000000"
                  value={totpCode}
                  onChange={e => setTotpCode(e.target.value.replace(/\D/g, ''))}
                  className="text-center text-2xl font-mono tracking-widest"
                  autoFocus required
                />
                <button type="submit" disabled={loading || totpCode.length !== 6}
                  style={{ backgroundColor: primaryColor }}
                  className="w-full py-3 text-white font-semibold rounded-xl disabled:opacity-60 transition-colors">
                  {loading ? 'Verifying...' : 'Verify Code'}
                </button>
                <button type="button" onClick={() => { setTwoFAStep(false); setTotpCode(''); }}
                  className="w-full py-2 text-slate-500 text-sm hover:text-slate-700">
                  ← Back to login
                </button>
              </form>
            </>

          /* ── Normal login ── */
          ) : (
            <>
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Sign In to Your Account</h2>
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2" htmlFor="email">
                    Email Address
                  </label>
                  <BrandedInput
                    primaryColor={primaryColor}
                    id="email" type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    data-testid="login-email-input"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2" htmlFor="password">
                    Password
                  </label>
                  <div className="relative">
                    <BrandedInput
                      primaryColor={primaryColor}
                      id="password"
                      type={showPass ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      data-testid="login-password-input"
                      className="pr-12"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPass(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                      data-testid="toggle-password-visibility"
                    >
                      {showPass ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="login-submit-button"
                  style={{ backgroundColor: primaryColor }}
                  className="w-full text-white py-3 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:opacity-90"
                >
                  {loading ? 'Signing in...' : 'Sign In'}
                  {!loading && <ArrowRight size={20} />}
                </button>
              </form>

              <div className="mt-6 text-center">
                <Link
                  to="/forgot-password"
                  className="text-sm transition-colors hover:underline"
                  style={{ color: primaryColor }}
                  data-testid="forgot-password-link"
                >
                  Forgot your password?
                </Link>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-6 space-y-1">
          <p className="text-slate-500 text-sm">© {new Date().getFullYear()} {companyName}</p>
          {!isClient && (branding?.white_label ? branding.white_label.show_powered_by !== false : true) && (
            // Phase W1 — white-label tenants show the PARTNER's platform
            // mark ("Powered by Avanti Labs"); Euphoric never appears on
            // a partner-apex host. W2.2 — the partner can switch the line
            // off per client from their console.
            <p className="text-slate-400 text-xs">
              Powered by {branding?.white_label?.platform_name || 'Euphoric AI'}
            </p>
          )}
        </div>

      </div>
    </div>
  );
}
